import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Avatar } from "@mui/material";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  getPlayerDisplayName,
  resolvePlayerKey,
} from "../utils/sessionResultsPlayers";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

function SessionPlayerTile({ player, selected, onSelect, label, isAll = false }) {
  const name = isAll ? label : getPlayerDisplayName(player) || "—";

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.8,
        p: 1.2,
        minHeight: 96,
        borderRadius: 3,
        cursor: "pointer",
        border: selected
          ? "2px solid var(--brand-primary, #0877BC)"
          : "1px solid var(--border-color)",
        bgcolor: selected
          ? "rgba(var(--brand-primary-rgb), 0.12)"
          : "var(--surface-color)",
        boxShadow: selected
          ? "0 8px 20px rgba(var(--brand-primary-rgb), 0.12)"
          : "0 2px 8px var(--shadow-color)",
        transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
        "&:active": { transform: "scale(0.97)" },
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          fontSize: "0.9rem",
          fontWeight: 900,
          bgcolor: selected ? "var(--brand-primary, #0877BC)" : "rgba(var(--brand-primary-rgb), 0.1)",
          color: selected ? "#fff" : "var(--brand-primary, #0877BC)",
        }}
      >
        {isAll ? <AutoAwesomeIcon sx={{ fontSize: 20 }} /> : getInitials(name)}
      </Avatar>
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: selected ? 800 : 700,
          color: selected ? "var(--brand-primary, #0877BC)" : "var(--text-color)",
          textAlign: "center",
          lineHeight: 1.2,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

export function SessionPlayerFilter({ players, selectedPlayerId, onSelectPlayer, loading = false }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 2.5, px: 0.5 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color: "var(--text-color)",
          mb: 1.2,
          fontSize: "0.9rem",
          display: "flex",
          alignItems: "center",
          gap: 0.8,
        }}
      >
        <LocalActivityIcon style={{ fontSize: 16, color: "var(--brand-primary, #0877BC)" }} />
        {t("player_filter", "Filter by Player")}
      </Typography>
      {loading ? (
        <Typography variant="body2" sx={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
          {t("loading") || "Loading..."}
        </Typography>
      ) : players.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(3, minmax(0, 1fr))",
              sm: "repeat(4, minmax(0, 1fr))",
              md: "repeat(5, minmax(0, 1fr))",
            },
            gap: 1.2,
          }}
        >
          <SessionPlayerTile
            isAll
            label={t("all_players", "All Players")}
            selected={selectedPlayerId === "all"}
            onSelect={() => onSelectPlayer("all")}
          />
          {players.map((player) => {
            const pId = player._filterKey || resolvePlayerKey(player);
            if (!pId) return null;
            return (
              <SessionPlayerTile
                key={pId}
                player={player}
                selected={selectedPlayerId === pId}
                onSelect={() => onSelectPlayer(pId)}
              />
            );
          })}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", fontSize: "0.85rem", color: "var(--text-secondary)" }}
        >
          {t("noPlayersInSession", "No players found for this session.")}
        </Typography>
      )}
    </Box>
  );
}
